/* eslint-disable */
import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  CheckCircle,
  Warning,
  TrendingUp,
  HelpOutline,
} from '@mui/icons-material';

export type DeliveryStatus = 'on_track' | 'delayed' | 'ahead' | 'unknown';

interface DeliveryStatusBadgeProps {
  status: DeliveryStatus;
  size?: 'small' | 'medium';
  showIcon?: boolean;
  showLabel?: boolean;
}

export const DeliveryStatusBadge: React.FC<DeliveryStatusBadgeProps> = ({
  status,
  size = 'small',
  showIcon = true,
  showLabel = true,
}) => {
  const getStatusConfig = (status: DeliveryStatus) => {
    switch (status) {
      case 'on_track':
        return {
          color: 'success' as const,
          label: 'On Track',
          icon: <CheckCircle fontSize={size === 'small' ? 'small' : 'medium'} />,
          tooltip: 'All stories are scheduled on or before the expected version',
        };
      case 'delayed':
        return {
          color: 'error' as const,
          label: 'Delayed',
          icon: <Warning fontSize={size === 'small' ? 'small' : 'medium'} />,
          tooltip: 'Some stories are scheduled after the expected version',
        };
      case 'ahead':
        return {
          color: 'info' as const,
          label: 'Ahead',
          icon: <TrendingUp fontSize={size === 'small' ? 'small' : 'medium'} />,
          tooltip: 'All stories are scheduled before the expected version',
        };
      default:
        return {
          color: 'default' as const,
          label: 'Unknown',
          icon: <HelpOutline fontSize={size === 'small' ? 'small' : 'medium'} />,
          tooltip: 'Delivery status cannot be determined (no expected version or no stories)',
        };
    }
  };

  const config = getStatusConfig(status);

  const chip = (
    <Chip
      icon={showIcon ? config.icon : undefined}
      label={showLabel ? config.label : undefined}
      color={config.color}
      size={size}
      sx={{
        minWidth: showLabel ? '100px' : 'auto',
        fontWeight: 'bold',
        '& .MuiChip-icon': {
          color: 'inherit',
        },
      }}
      aria-label={`Delivery status: ${config.label}`}
    />
  );

  if (showLabel) {
    return chip;
  }

  return (
    <Tooltip title={config.tooltip} arrow placement="top">
      {chip}
    </Tooltip>
  );
};
